"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { LiquidPill } from "./liquid-pill";
import { V2_MOTION } from "./motion";
import { Z_INDEX } from "./z-index";

const OPEN_MS = V2_MOTION.duration.revealOpen;
const CLOSE_MS = V2_MOTION.duration.revealClose;

export interface ImageZoomRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface ImageZoomProps {
  open: boolean;
  src: string | null;
  sourceRect: ImageZoomRect | null;
  onClose: () => void;
  alt?: string;
  closeButtonLabel?: string;
  className?: string;
}

interface ZoomSession {
  src: string;
  from: ImageZoomRect;
}

function cloneRect(rect: ImageZoomRect): ImageZoomRect {
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function getAspectRatio(rect: ImageZoomRect) {
  if (rect.width > 0 && rect.height > 0) {
    return rect.width / rect.height;
  }
  return 1;
}

function getExpandedRect(sourceRect: ImageZoomRect): ImageZoomRect {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const aspectRatio = getAspectRatio(sourceRect);

  const maxWidth = viewportWidth * 0.92;
  const maxHeight = viewportHeight * 0.88;

  let width = maxWidth;
  let height = width / aspectRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return {
    top: (viewportHeight - height) / 2,
    left: (viewportWidth - width) / 2,
    width,
    height,
  };
}

function rectToKeyframe(rect: ImageZoomRect, borderRadius: string) {
  return {
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    borderRadius,
  };
}

function readRect(element: HTMLElement): ImageZoomRect {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function runRectAnimation(
  element: HTMLElement,
  from: ImageZoomRect,
  to: ImageZoomRect,
  duration: number,
  easing: string,
  borderRadiusFrom: string,
  borderRadiusTo: string
) {
  element.getAnimations().forEach((animation) => animation.cancel());

  element.style.position = "fixed";
  element.style.margin = "0";
  element.style.padding = "0";
  element.style.overflow = "hidden";
  element.style.transform = "none";
  element.style.top = `${from.top}px`;
  element.style.left = `${from.left}px`;
  element.style.width = `${from.width}px`;
  element.style.height = `${from.height}px`;
  element.style.borderRadius = borderRadiusFrom;

  if (duration <= 0) {
    element.style.top = `${to.top}px`;
    element.style.left = `${to.left}px`;
    element.style.width = `${to.width}px`;
    element.style.height = `${to.height}px`;
    element.style.borderRadius = borderRadiusTo;
    return Promise.resolve();
  }

  void element.getBoundingClientRect();

  const animation = element.animate(
    [
      rectToKeyframe(from, borderRadiusFrom),
      rectToKeyframe(to, borderRadiusTo),
    ],
    {
      duration,
      easing,
      fill: "forwards",
    }
  );

  return new Promise<void>((resolve) => {
    const finish = () => {
      animation.removeEventListener("finish", finish);
      animation.removeEventListener("cancel", finish);
      resolve();
    };

    animation.addEventListener("finish", finish);
    animation.addEventListener("cancel", finish);
  });
}

export function resolveImageZoomTarget(target: HTMLElement): {
  src: string;
  sourceRect: ImageZoomRect;
} | null {
  const picture = target.closest("picture");
  const img =
    target instanceof HTMLImageElement
      ? target
      : target.querySelector("img");

  const host = picture ?? img;
  if (!host) return null;

  const src =
    picture?.getAttribute("data-image-url") ??
    (img instanceof HTMLImageElement ? img.getAttribute("src") : null);

  if (!src) return null;

  const rect = host.getBoundingClientRect();

  return {
    src,
    sourceRect: {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    },
  };
}

export function ImageZoom({
  open,
  src,
  sourceRect,
  onClose,
  alt = "Vista previa de imagen",
  closeButtonLabel = "Cerrar",
  className,
}: ImageZoomProps) {
  const [portalReady, setPortalReady] = React.useState(false);
  const [backdropVisible, setBackdropVisible] = React.useState(false);
  const [showCloseButton, setShowCloseButton] = React.useState(false);

  const frameRef = React.useRef<HTMLDivElement>(null);
  const sessionRef = React.useRef<ZoomSession | null>(null);
  const phaseRef = React.useRef<"idle" | "opening" | "open" | "closing">("idle");

  React.useEffect(() => {
    if (!open || !src || !sourceRect) return;

    sessionRef.current = {
      src,
      from: cloneRect(sourceRect),
    };
    phaseRef.current = "opening";
    setPortalReady(true);
    setBackdropVisible(false);
    setShowCloseButton(false);
  }, [open, src, sourceRect]);

  React.useLayoutEffect(() => {
    if (!portalReady || !open || phaseRef.current !== "opening") return;

    const frame = frameRef.current;
    const session = sessionRef.current;
    if (!frame || !session) return;

    let cancelled = false;

    const run = async () => {
      setBackdropVisible(true);

      await runRectAnimation(
        frame,
        session.from,
        getExpandedRect(session.from),
        OPEN_MS,
        V2_MOTION.ease.premiumReveal,
        "12px",
        "16px"
      );

      if (cancelled) return;

      phaseRef.current = "open";
      setShowCloseButton(true);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [portalReady, open]);

  React.useEffect(() => {
    if (open || !portalReady || phaseRef.current === "closing") return;

    const frame = frameRef.current;
    const session = sessionRef.current;
    if (!frame || !session) {
      setPortalReady(false);
      phaseRef.current = "idle";
      return;
    }

    let cancelled = false;
    phaseRef.current = "closing";
    setShowCloseButton(false);
    setBackdropVisible(false);

    const run = async () => {
      const current = readRect(frame);

      await runRectAnimation(
        frame,
        current,
        session.from,
        CLOSE_MS,
        V2_MOTION.ease.standard,
        "16px",
        "12px"
      );

      if (cancelled) return;

      phaseRef.current = "idle";
      sessionRef.current = null;
      setPortalReady(false);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [open, portalReady]);

  React.useEffect(() => {
    if (!portalReady) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [portalReady]);

  React.useEffect(() => {
    if (!portalReady) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, portalReady]);

  if (!portalReady || !sessionRef.current) return null;

  return createPortal(
    <div
      className={cn("fixed inset-0", className)}
      style={{ zIndex: Z_INDEX.MODAL }}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      <button
        type="button"
        aria-label={closeButtonLabel}
        className="absolute inset-0 bg-white/60 backdrop-blur-md transition-opacity duration-300 dark:bg-zinc-950/60"
        style={{
          opacity: backdropVisible ? 1 : 0,
          zIndex: Z_INDEX.MODAL_BACKDROP,
        }}
        onClick={onClose}
      />

      <div
        ref={frameRef}
        className="shadow-[0_24px_80px_-24px_rgba(15,23,42,0.45)] dark:shadow-[0_24px_80px_-24px_rgba(0,0,0,0.75)]"
        style={{ zIndex: Z_INDEX.MODAL }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={sessionRef.current.src}
          alt={alt}
          className="block h-full w-full object-cover"
          draggable={false}
        />
      </div>

      <div
        className="pointer-events-auto absolute right-5 top-5 sm:right-6 sm:top-6"
        style={{
          zIndex: Z_INDEX.MODAL,
          opacity: showCloseButton ? 1 : 0,
          transform: showCloseButton ? "translateY(0px)" : "translateY(-8px)",
          transition: `opacity 240ms ${V2_MOTION.ease.premiumReveal}, transform 240ms ${V2_MOTION.ease.premiumReveal}`,
        }}
      >
        <LiquidPill
          as="button"
          type="button"
          onClick={onClose}
          aria-label={closeButtonLabel}
          variant="rose"
          appearance="tinted"
          shape="circle"
          size="lg"
          icon={<X className="h-5 w-5" />}
        />
      </div>
    </div>,
    document.body
  );
}
